import React from 'react';
import {createContext, useContext, useState, useRef} from 'react';
import {PAGE_TYPES} from './../localization/config';
import PageNameService from './../PageNameService';
import {AnalyticsContextType, DataStore, TrackingData} from './type';
import AnalyticService from '../index';
import {EventSchema} from '../analytics.types';

const initialDataStore: DataStore = {
  analyticData: {
    [PAGE_TYPES.PROFILE as string]: {
      global: {},
      page: {},
    },
    [PAGE_TYPES.REFUND_RAISE_REQUEST as string]: {
      global: {},
      page: {},
    },
    [PAGE_TYPES.SUPPLIER_SETTINGS as string]: {
      global: {},
      page: {},
    },
    [PAGE_TYPES.GST_INVOICE_REPORT as string]: {
      global: {},
      page: {},
    },
    [PAGE_TYPES.COMMISSION_REPORT as string]: {
      global: {},
      page: {},
    },
  },
};

const AnalyticsServiceContext = createContext<AnalyticsContextType | null>(null);

const AnalyticsServiceProvider = ({children}: {children: React.ReactNode}) => {
  const dataStoreRef = useRef<DataStore>(initialDataStore);
  const [, forceUpdate] = useState({});

  const updateAnalyticDataStore = (trackingData: TrackingData, pageName?: string) => {
    const currentPageName = pageName || PageNameService.getCurrentPageName();

    if (!currentPageName) {
      return;
    }

    const newState = {
      ...dataStoreRef.current,
      analyticData: {
        ...dataStoreRef.current.analyticData,
        [currentPageName]: {
          global: trackingData?.global?.payload
            ? trackingData.global.payload
            : dataStoreRef.current.analyticData[currentPageName as string]?.global || {},
          page: trackingData?.pageLoad?.payload
            ? trackingData?.pageLoad.payload
            : dataStoreRef.current.analyticData[currentPageName as string]?.page || {},
        },
      },
    };

    dataStoreRef.current = newState;
  };

  const sendToSdk = (pageName?: string) => {
    const currentPageName = pageName || PageNameService.getCurrentPageName();

    if (!currentPageName) {
      console.warn('No page name provided and could not determine current page');
      return;
    }

    const currentDataStore = dataStoreRef.current;
    const pageData = currentDataStore.analyticData[currentPageName as string];

    if (
      !pageData ||
      (!Object.keys(pageData.global || {}).length && !Object.keys(pageData.page || {}).length)
    ) {
      console.warn(
        `No data available for page: ${currentPageName as string}. Data might not be updated yet.`,
      );
      return;
    }

    const sdkData = {
      ...pageData.global,
      ...pageData.page,
    };
    console.log('sdkData', sdkData);
    AnalyticService.trackAll(sdkData as EventSchema);
  };

  const sendTrackingDataToSdk = (trackingData: Record<string, any>, pageName?: string) => {
    const currentPageName = pageName || PageNameService.getCurrentPageName();

    if (!currentPageName) {
      console.warn('No page name provided and could not determine current page');
      return;
    }

    const currentDataStore = dataStoreRef.current;
    const pageData = currentDataStore.analyticData[currentPageName as string];

    if (!pageData) {
      console.warn(
        `No data available for page: ${currentPageName as string}. Data might not be updated yet.`,
      );
      return;
    }

    const sdkData = {
      ...pageData.global,
      ...pageData.page,
      ...trackingData,
    };
    console.log('sdkData with tracking data', sdkData);
    AnalyticService.trackAll(sdkData as EventSchema);
  };

  return (
    <AnalyticsServiceContext.Provider
      value={{
        dataStore: dataStoreRef.current,
        updateAnalyticDataStore,
        sendToSdk,
        sendTrackingDataToSdk,
        forceUpdate,
      }}
    >
      {children}
    </AnalyticsServiceContext.Provider>
  );
};

const useAnalyticService = (): AnalyticsContextType => {
  const context = useContext(AnalyticsServiceContext);
  if (!context) {
    throw new Error('useAnalyticService must be used within an AnalyticsServiceProvider');
  }
  return context;
};

export {AnalyticsServiceProvider, useAnalyticService};

export default AnalyticsServiceProvider;
